import { create } from 'zustand';
import type { MemberSearchResult, PosCheckoutResult, PosProduct, PosVariant } from './api';
import { useAuthStore } from './auth-store';
import { lineStockCap } from './pos-pricing';

/**
 * The sale being rung up, held outside React so it survives the navigation
 * from the till to `/checkout` and on to `/receipt`. In memory only: a reload
 * starts an empty sale, exactly as the page-local state did before.
 *
 * Only what the cashier ENTERED lives here. Everything derived from it (totals,
 * fee pricing, which tenders are usable) is computed in `use-pos-sale.ts`, and
 * prices are never stored on a line: they are read at render so switching the
 * buyer re-prices lines already in the cart.
 */

export type SaleMode = 'retail' | 'stockist';

export type CartLine = {
  product: PosProduct;
  /** Chosen variation for a variation product; null for single-SKU lines. */
  variant: PosVariant | null;
  qty: number;
};

export type FeeRow = { typeCode: string; amount: string };

export type ManagerDiscountDraft = {
  pct: string;
  amount: string;
  reason: string;
  /** Set after a successful manager PIN verify when the cap is exceeded. */
  approvalChip: string | null;
};

/**
 * The ONE coupon a sale may carry: a preset the cashier pressed, a code the
 * customer handed over, or one of the buyer's gift checks. Picking any of them
 * replaces the others. The cashier's typed discount (`ManagerDiscountDraft`) is
 * a separate layer and may sit on top.
 */
export type SaleCoupon =
  | { kind: 'preset'; id: string; name: string; requiresManagerPin: boolean }
  | { kind: 'code'; code: string }
  | { kind: 'voucher'; id: string; code: string; name: string };

/** A gift check belongs to its buyer: another buyer on the sale drops it. */
function couponForBuyer(coupon: SaleCoupon | null, sameBuyer: boolean) {
  return coupon?.kind === 'voucher' && !sameBuyer ? { coupon: null, couponChip: null } : {};
}

// ── cart line rules (pure) ────────────────────────────────────────────

/** Stable cart-line identity: a product+variant pair is its own line. */
export function lineKey(l: { product: { id: string }; variant: { id: string } | null }): string {
  return l.variant ? `${l.product.id}:${l.variant.id}` : l.product.id;
}

/**
 * The most units of a line the till will ring up: its sellable stock, and 1
 * for a course. A course grants ONE enrollment however many times it is rung
 * up, so the server pins course lines to qty 1 (`course_qty_must_be_one`).
 */
export function lineCap(l: { product: PosProduct; variant: PosVariant | null }): number {
  return l.product.isCourse ? 1 : lineStockCap(l);
}

/**
 * Add `qty` of a product (or one of its variations). Tops up the existing line
 * when there is one, clamped to what can be sold; returns the SAME array when
 * nothing can be added, so a tap on a sold-out tile is a no-op.
 */
export function addLine(
  cart: CartLine[],
  product: PosProduct,
  variant: PosVariant | null,
  qty = 1,
): CartLine[] {
  if (qty < 1) return cart;
  const key = lineKey({ product, variant });
  const cap = lineCap({ product, variant });
  const existing = cart.find((l) => lineKey(l) === key);
  if (existing) {
    const next = Math.min(existing.qty + qty, cap);
    if (next <= existing.qty) return cart;
    return cart.map((l) => (lineKey(l) === key ? { ...l, qty: next } : l));
  }
  if (cap < 1) return cart;
  return [...cart, { product, variant, qty: Math.min(qty, cap) }];
}

/** − / + on a line. Stepping below 1 removes it; stepping past stock is a no-op. */
export function stepLine(cart: CartLine[], key: string, delta: number): CartLine[] {
  return cart.flatMap((l) => {
    if (lineKey(l) !== key) return [l];
    const next = l.qty + delta;
    if (next <= 0) return [];
    if (next > lineCap(l)) return [l];
    return [{ ...l, qty: next }];
  });
}

/**
 * Absolute set, for the typed quantity field. Unlike `stepLine` a typed value
 * NEVER removes the line: it floors at 1, because a mistyped `0` at a busy till
 * silently dropping a product is worse than a no-op. Use − at qty 1, or the
 * remove control, to clear a line.
 */
export function setLineQty(cart: CartLine[], key: string, n: number): CartLine[] {
  return cart.map((l) =>
    lineKey(l) === key ? { ...l, qty: Math.min(Math.max(n, 1), lineCap(l)) } : l,
  );
}

export function removeLine(cart: CartLine[], key: string): CartLine[] {
  return cart.filter((l) => lineKey(l) !== key);
}

// ── store ─────────────────────────────────────────────────────────────

const EMPTY_DISCOUNT: ManagerDiscountDraft = {
  pct: '',
  amount: '',
  reason: '',
  approvalChip: null,
};

/**
 * A fresh idempotency key for one sale. It is sent with the checkout, and the
 * server answers a second request carrying the same key with the FIRST order
 * instead of charging again. So it lasts exactly as long as the sale does: a
 * retry of the same sale (double tap, a response lost on a bad connection,
 * leaving `/checkout` mid-charge and coming back) reuses it, and the next sale
 * gets a new one.
 */
function newSaleKey(): string {
  // `randomUUID` exists only in a secure context (https or localhost). A till
  // opened over plain http on a shop LAN must still load, so fall back to
  // `getRandomValues`, which is available everywhere.
  if (typeof crypto.randomUUID === 'function') return `pos-${crypto.randomUUID()}`;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `pos-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/** Everything one sale carries. `mode` and the branch outlive it. */
const emptySale = () => ({
  saleKey: newSaleKey(),
  cart: [] as CartLine[],
  member: null as MemberSearchResult | null,
  slotId: null as string | null,
  slotCode: null as string | null,
  /** Null re-defaults to the first payment method once the registry is in. */
  methodCode: null as string | null,
  fees: [] as FeeRow[],
  amountTendered: '',
  remarks: '',
  managerDiscount: EMPTY_DISCOUNT,
  coupon: null as SaleCoupon | null,
  /** Manager approval for a preset marked "needs a manager PIN". */
  couponChip: null as string | null,
});

type SaleState = ReturnType<typeof emptySale> & {
  mode: SaleMode;
  /** An admin's branch pick. A scoped cashier's branch comes from their login. */
  selectedBranchId: string | null;
  /** The sale that just completed, for the receipt screen. */
  receipt: PosCheckoutResult | null;
  /**
   * A checkout request is in flight. Held here rather than in the page because
   * the page can be left and re-entered while the request is still out.
   */
  charging: boolean;
  setCharging: (charging: boolean) => void;

  /** Entering the other tab starts a fresh sale: the two price differently. */
  setMode: (mode: SaleMode) => void;
  /** Changing branch starts a fresh sale: the cart holds that branch's stock. */
  selectBranch: (id: string | null) => void;

  addToCart: (product: PosProduct, variant?: PosVariant | null, qty?: number) => void;
  stepQty: (key: string, delta: number) => void;
  setQty: (key: string, n: number) => void;
  removeFromCart: (key: string) => void;

  /** A slot match binds its slot; a person match leaves the slot to be picked. */
  pickMember: (m: MemberSearchResult) => void;
  setSlot: (slotId: string, slotCode: string) => void;
  clearMember: () => void;

  setMethodCode: (code: string | null) => void;
  setFees: (next: FeeRow[] | ((prev: FeeRow[]) => FeeRow[])) => void;
  setAmountTendered: (v: string) => void;
  setRemarks: (v: string) => void;
  patchManagerDiscount: (patch: Partial<ManagerDiscountDraft>) => void;
  /** Press a preset, apply a code, or (null) take the coupon off. */
  setCoupon: (coupon: SaleCoupon | null) => void;
  setCouponChip: (chip: string | null) => void;

  /** Drop the whole sale in progress (the "Clear sale" control). */
  clearSale: () => void;
  /** A sale went through: keep its receipt, start the next sale empty. */
  completeSale: (receipt: PosCheckoutResult) => void;
  dismissReceipt: () => void;
};

export const useSaleStore = create<SaleState>((set) => ({
  ...emptySale(),
  mode: 'retail',
  selectedBranchId: null,
  receipt: null,
  charging: false,
  setCharging: (charging) => set({ charging }),

  setMode: (mode) => set((s) => (s.mode === mode ? s : { ...emptySale(), mode })),
  selectBranch: (id) =>
    set((s) =>
      s.selectedBranchId === id
        ? s
        : // The very first pick (nothing selected yet) is not a change of branch.
          s.selectedBranchId === null
          ? { selectedBranchId: id }
          : { ...emptySale(), selectedBranchId: id },
    ),

  addToCart: (product, variant = null, qty = 1) =>
    set((s) => ({ cart: addLine(s.cart, product, variant, qty) })),
  stepQty: (key, delta) => set((s) => ({ cart: stepLine(s.cart, key, delta) })),
  setQty: (key, n) => set((s) => ({ cart: setLineQty(s.cart, key, n) })),
  removeFromCart: (key) => set((s) => ({ cart: removeLine(s.cart, key) })),

  pickMember: (m) =>
    set((s) => ({
      ...(m.matchType === 'slot' && m.slotId
        ? { member: m, slotId: m.slotId, slotCode: m.slotCode }
        : { member: m, slotId: null, slotCode: null }),
      ...couponForBuyer(s.coupon, s.member?.id === m.id),
    })),
  setSlot: (slotId, slotCode) => set({ slotId, slotCode }),
  clearMember: () =>
    set((s) => ({
      member: null,
      slotId: null,
      slotCode: null,
      ...couponForBuyer(s.coupon, false),
    })),

  setMethodCode: (methodCode) => set({ methodCode }),
  setFees: (next) => set((s) => ({ fees: typeof next === 'function' ? next(s.fees) : next })),
  setAmountTendered: (amountTendered) => set({ amountTendered }),
  setRemarks: (remarks) => set({ remarks }),
  patchManagerDiscount: (patch) =>
    set((s) => ({ managerDiscount: { ...s.managerDiscount, ...patch } })),
  // An approval belongs to the coupon it was given for, so a change of coupon
  // always drops it.
  setCoupon: (coupon) => set({ coupon, couponChip: null }),
  setCouponChip: (couponChip) => set({ couponChip }),

  clearSale: () => set(emptySale()),
  completeSale: (receipt) => set({ ...emptySale(), receipt }),
  dismissReceipt: () => set({ receipt: null }),
}));

// A sale belongs to the cashier who started it. When the page-local state
// lived in the component it died with the sign-out; here it has to be dropped
// explicitly, or the next person to sign in on this tab inherits the cart and
// the selected member.
useAuthStore.subscribe((auth, prev) => {
  if (prev.token && !auth.token) {
    useSaleStore.setState({
      ...emptySale(),
      selectedBranchId: null,
      receipt: null,
      charging: false,
    });
  }
});
